import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import {
  AUTH_EVENTS,
  getSessionReadyFromSso,
  useSso,
} from '@discover-frontend/auth';
import { useClearStorageOnMissingVersion } from 'src/modules/hydration-manager';
import { useTrialBootstrapGate } from 'src/modules/trial-paywall';
import { isTrialUser } from 'src/utils/AuthHelperMethods';
import {
  requestMe,
  getMeProducts,
  requestFusions,
  requestAudienceBuilders,
} from '../../actions';
import { InitLayout } from '../../layouts';

const LOGOUT_PATH = '/logout';

const hasMeProductsData = (meProducts) => {
  return Array.isArray(meProducts?.data) && meProducts.data.length > 0;
};

const InitContainer = ({ children, isExternalPath, logged }) => {
  const dispatch = useDispatch();
  const me = useSelector((state) => state.me);
  const meProducts = useSelector((state) => state.meProducts);
  const sso = useSso();
  const { pathname } = useLocation();
  const sessionReady = getSessionReadyFromSso(sso);
  const isSessionLocked = sso.sessionPhase === 'locked';

  const { canBootstrapSession, isTrialBootstrapBlocked } =
    useTrialBootstrapGate({
      isTrial: !isExternalPath && isTrialUser(),
      sessionReady: Boolean(logged) && sessionReady,
    });

  const canFetchSessionData = !isExternalPath && canBootstrapSession;
  const hasUserData = Boolean(me.data?.email);
  const shouldBootstrapSession = canFetchSessionData && !hasUserData;

  useClearStorageOnMissingVersion(shouldBootstrapSession);

  useEffect(() => {
    if (!canFetchSessionData) return;

    if (!hasUserData) {
      dispatch(requestMe());
    }

    if (!hasMeProductsData(meProducts)) {
      dispatch(getMeProducts());
    }
  }, [canFetchSessionData, dispatch, hasUserData, meProducts.data]);

  useEffect(() => {
    if (isExternalPath) return;

    if (me.logout && !isSessionLocked) {
      window.dispatchEvent(new Event(AUTH_EVENTS.AUTH_FAILURE));
    }
  }, [me.logout, isExternalPath, isSessionLocked]);

  const prevMeProductsIsLoadingRef = React.useRef(meProducts.isLoading);

  useEffect(() => {
    if (!canFetchSessionData) return;

    if (!meProducts.isLoading && prevMeProductsIsLoadingRef.current) {
      dispatch(requestFusions());
      dispatch(requestAudienceBuilders());
    }

    prevMeProductsIsLoadingRef.current = meProducts.isLoading;
  }, [canFetchSessionData, meProducts.isLoading, dispatch]);

  if (isExternalPath) return children;

  // Escape hatch: a locked-out trial never loads `me`, so without this the
  // logout route is unreachable and the user is stranded behind the modal.
  if (isTrialBootstrapBlocked && pathname === LOGOUT_PATH) return children;

  if (hasUserData) return children;

  if (isSessionLocked) return null;

  return <InitLayout />;
};

export default InitContainer;
